import { useEffect, useMemo, useState } from 'react';
import AppContext, { AppData, DynamicAppData, PriceTable, StaticAppData, ValueMapping } from './AppContext';
import View from './components/View';
import { getFirstUnrestrictedVariationWert } from './utils/app-data';
import { getKEigenschaftBreite,
  getKEigenschaftHoehe,
  getKEigenschaftLaenge,
  getKEigenschaftTiefe,
  getKEigenschaftMontageart,
  getKEigenschaftStauhoehe,
  getKEigenschaftVersand,
  getKEigenschaftVerstaerkung,
  getKEigenschaftWandschienenhoehe } from './utils/artikel';
import { parsePriceTableCsv, parseValueMappingCsv } from './utils/price';
import { getArtikelInfo, getArtikelKonfiguration, getKategorieInfo, getKEigenschaftWertToValueMapping, getPriceTable, getShopInfo } from './utils/plugin-api';
import { appConfig } from './config';

const INIT_STATIC_STATE: StaticAppData = {
  wawiData: appConfig.wawiData,
  artikel: undefined as unknown as Artikel,
  kategorie: undefined as unknown as Kategorie,
  config: undefined as unknown as ArtikelKonfiguration,
  shop: undefined as unknown as ShopKonfiguration,
  priceTableNetto: undefined as unknown as PriceTable,
  kEigenschaftWertToValue: undefined as unknown as ValueMapping,
  kEigenschaftBreite: undefined as unknown as number,
  kEigenschaftHoehe: undefined as unknown as number,
  kEigenschaftLaenge: undefined as unknown as number,
  kEigenschaftTiefe: undefined as unknown as number,
  kEigenschaftVersand: undefined as unknown as number,
  kEigenschaftMontageart: undefined as unknown as number,
  kEigenschaftStauhoehe: undefined as unknown as number,
  kEigenschaftWandschienenhoehe: undefined as unknown as number,
  kEigenschaftVerstaerkung: undefined as unknown as number,
};

const INIT_DYNAMIC_STATE: DynamicAppData = {
  invalid: false,
  selectedVariationWerte: {},
};

const INIT_STATE: AppData = {
  static: INIT_STATIC_STATE,
  dynamic: INIT_DYNAMIC_STATE,
};

function App() {
  const [staticAppData, setStaticAppData] = useState(INIT_STATIC_STATE);
  const [dynamicAppData, setDynamicAppData] = useState(INIT_DYNAMIC_STATE);
  const [appData, setAppData] = useState(INIT_STATE);
  const [initialized, setInitialized] = useState(false);
  useEffect(() => {
    const loadData = async () => {
      const shop = await getShopInfo();
      setStaticAppData((c) => ({
        ...c,
        shop,
      }));
      const kategorie = await getKategorieInfo(staticAppData.wawiData.kKategorie);
      setStaticAppData((c) => ({
        ...c,
        kategorie,
      }));
      const config = await getArtikelKonfiguration(staticAppData.wawiData.kArtikel);
      setStaticAppData((c) => ({
        ...c,
        config,
      }));
      if (config.productType === 'hochwasserschutz'
        || config.productType === 'insektenschutz') {
        const priceTableConfig = await getPriceTable(staticAppData.wawiData.kArtikel);
        const priceTableNetto = parsePriceTableCsv(priceTableConfig);
        setStaticAppData((c) => ({
          ...c,
          priceTableNetto,
        }));
      }
      if (config.productType === 'hochwasserschutz') {
        const valueMappingCsv = await getKEigenschaftWertToValueMapping(staticAppData.wawiData.kArtikel);
        const kEigenschaftWertToValue = await parseValueMappingCsv(valueMappingCsv);
        setStaticAppData((c) => ({
          ...c,
          kEigenschaftWertToValue,
        }));
      }
      const artikel = await getArtikelInfo(staticAppData.wawiData.kArtikel);
      if (config.productType === 'rolladen') {
        const kEigenschaftBreite = getKEigenschaftBreite(artikel);
        const kEigenschaftHoehe = getKEigenschaftHoehe(artikel);
        const kEigenschaftVersand = getKEigenschaftVersand(artikel);
        setStaticAppData((a) => ({
          ...a,
          artikel: {
            ...artikel,
            variationen: artikel.variationen.sort((v1, v2) => v1.nSort - v2.nSort),
          },
          kEigenschaftBreite,
          kEigenschaftHoehe,
          kEigenschaftVersand,
        }));
      } else if (config.productType === 'stahlwelle'
        || config.productType === 'lamelle'
        || config.productType === 'fuehrungsschiene'
        || config.productType === 'endleiste'
        || config.productType === 'deckleiste') {
        const kEigenschaftLaenge = getKEigenschaftLaenge(artikel);
        const kEigenschaftVersand = getKEigenschaftVersand(artikel);
        setStaticAppData((a) => ({
          ...a,
          artikel: {
            ...artikel,
            variationen: artikel.variationen.sort((v1, v2) => v1.nSort - v2.nSort),
          },
          kEigenschaftLaenge,
          kEigenschaftVersand,
        }));
      } else if (config.productType === 'hochwasserschutz') {
        const kEigenschaftMontageart = getKEigenschaftMontageart(artikel);
        const kEigenschaftBreite = getKEigenschaftBreite(artikel);
        const kEigenschaftStauhoehe = getKEigenschaftStauhoehe(artikel);
        const kEigenschaftWandschienenhoehe = getKEigenschaftWandschienenhoehe(artikel);
        const kEigenschaftVerstaerkung = getKEigenschaftVerstaerkung(artikel);
        const kEigenschaftVersand = getKEigenschaftVersand(artikel);
        setStaticAppData((a) => ({
          ...a,
          artikel: {
            ...artikel,
            variationen: artikel.variationen.sort((v1, v2) => v1.nSort - v2.nSort),
          },
          kEigenschaftMontageart,
          kEigenschaftBreite,
          kEigenschaftStauhoehe,
          kEigenschaftWandschienenhoehe,
          kEigenschaftVerstaerkung,
          kEigenschaftVersand,
        }));
      } else if (config.productType === 'insektenschutz') {
        const kEigenschaftBreite = getKEigenschaftBreite(artikel);
        const kEigenschaftTiefe = getKEigenschaftTiefe(artikel);
        setStaticAppData((a) => ({
          ...a,
          artikel: {
            ...artikel,
            variationen: artikel.variationen.sort((v1, v2) => v1.nSort - v2.nSort),
          },
          kEigenschaftBreite,
          kEigenschaftTiefe,
        }));
      } else {
        throw new Error(`Unknown product type: ${config.productType}`);
      }
    };
    loadData();
  }, [staticAppData.wawiData.kArtikel, staticAppData.wawiData.kKategorie]);
  useEffect(() => {
    if (staticAppData.artikel && staticAppData.config && staticAppData.kategorie) {
      const {
        kEigenschaftBreite,
        kEigenschaftHoehe,
        kEigenschaftLaenge,
        kEigenschaftTiefe,
        kEigenschaftWandschienenhoehe,
        kEigenschaftVerstaerkung,
      } = staticAppData;
      const variationWerte = {
        ...staticAppData.artikel.variationen
          .filter((variation) => variation.kEigenschaft !== kEigenschaftBreite
            && variation.kEigenschaft !== kEigenschaftHoehe
            && variation.kEigenschaft !== kEigenschaftLaenge
            && variation.kEigenschaft !== kEigenschaftTiefe
            && variation.kEigenschaft !== kEigenschaftVerstaerkung)
          .map((variation) => getFirstUnrestrictedVariationWert(staticAppData.artikel, staticAppData.config, {}, variation.kEigenschaft))
          .reduce((acc, variationWert) => {
            acc[variationWert.kEigenschaft] = variationWert.kEigenschaftWert;
            return acc;
          }, {} as ArtikelVariationWerte),
      };
      // Zweiter Lauf um ggf. ungültige Vorbelegungen aus dem ersten Lauf zu beheben
      const variationWerte2ndRun = {
        ...staticAppData.artikel.variationen
          .filter((variation) => variation.kEigenschaft !== kEigenschaftBreite
            && variation.kEigenschaft !== kEigenschaftHoehe
            && variation.kEigenschaft !== kEigenschaftLaenge
            && variation.kEigenschaft !== kEigenschaftTiefe
            && variation.kEigenschaft !== kEigenschaftVerstaerkung)
          .map((variation) => getFirstUnrestrictedVariationWert(staticAppData.artikel, staticAppData.config, variationWerte, variation.kEigenschaft))
          .reduce((acc, variationWert) => {
            acc[variationWert.kEigenschaft] = variationWert.kEigenschaftWert;
            return acc;
          }, {} as ArtikelVariationWerte),
      };
      if (kEigenschaftBreite) {
        variationWerte2ndRun[kEigenschaftBreite] = 1000;
      }
      if (kEigenschaftHoehe) {
        variationWerte2ndRun[kEigenschaftHoehe] = 1000;
      }
      if (kEigenschaftLaenge) {
        variationWerte2ndRun[kEigenschaftLaenge] = 1000;
      }
      if (kEigenschaftTiefe) {
        variationWerte2ndRun[kEigenschaftTiefe] = 400;
      }
      if (kEigenschaftWandschienenhoehe) {
        variationWerte2ndRun[kEigenschaftWandschienenhoehe] = 1000;
      }
      setDynamicAppData((a) => ({
        ...a,
        selectedVariationWerte: {
          ...variationWerte,
          ...variationWerte2ndRun,
        },
      }));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [staticAppData.artikel, staticAppData.config, staticAppData.kategorie]);
  useEffect(() => {
    if (Object.keys(dynamicAppData.selectedVariationWerte).length > 0) {
      setInitialized(true);
    }
  }, [dynamicAppData.selectedVariationWerte]);
  useEffect(() => {
    setAppData({
      static: staticAppData,
      dynamic: dynamicAppData,
    });
  }, [staticAppData, dynamicAppData]);
  const appDataMemo = useMemo(() => ({ appData, setDynamicAppData }), [appData, setDynamicAppData]);
  if (!initialized) {
    return <div>Laden...</div>;
  }
  if (appData.static.config.productType === 'rolladen'
    && (!appData.static.kEigenschaftBreite || !appData.static.kEigenschaftHoehe)
  ) {
    return <div>Die Variation _Breite_ oder _Höhe_ wurde nicht im Rolladenkonfigurator-Artikel gefunden</div>;
  }
  if ((appData.static.config.productType === 'deckleiste'
    || appData.static.config.productType === 'endleiste'
    || appData.static.config.productType === 'fuehrungsschiene'
    || appData.static.config.productType === 'lamelle'
    || appData.static.config.productType === 'stahlwelle')
    && !appData.static.kEigenschaftLaenge
  ) {
    return <div>Die Variation _Länge_ wurde nicht im Längenkonfigurator-Artikel gefunden</div>;
  }
  if (appData.static.config.productType === 'hochwasserschutz'
    && (!appData.static.kEigenschaftBreite || !appData.static.kEigenschaftMontageart
      || !appData.static.priceTableNetto || !appData.static.kEigenschaftWertToValue)
  ) {
    return (
      <div>Die Variation _Breite_ oder _Montageart_ oder die _Preistabelle_ oder das _EigenschaftMapping_ wurde
        nicht im Hochwasserschutzkonfigurator-Artikel gefunden
      </div>
    );
  }
  if (appData.static.config.productType === 'insektenschutz'
    && (!appData.static.kEigenschaftBreite || !appData.static.kEigenschaftTiefe
      || !appData.static.priceTableNetto)
  ) {
    return <div>Die Variation _Breite_ oder _Tiefe_ oder die _Preistabelle_ wurde nicht im Insektenschutzkonfigurator-Artikel gefunden</div>;
  }
  return (
    <AppContext.Provider value={appDataMemo}>
      <View />
    </AppContext.Provider>
  );
}

export default App;
